import http from "./http.service";

/**
 * Facturacion electronica con ARCA. Mismo contrato que `perfil.service` (la parte de ARCA) y
 * `factura.service` de tienda-admin (13b5a5b), con las rutas de autopartes:
 * `/api/cuenta/arca` (configuracion, pide MI_CUENTA) y `/api/cuenta/facturas` (pide FACTURACION).
 *
 * Sin cache a proposito: una factura es un hecho fiscal, siempre se lee del servidor.
 */

/** Cuando el request pide un archivo, el error tambien llega como Blob: se lee para sacar el mensaje. */
const errorDeBlob = async (err: any) => {
    if (err && err.message instanceof Blob) {
        try {
            const txt = await err.message.text();
            const json = JSON.parse(txt);
            return { ...err, message: json.message || json.error || "Ocurrió un error" };
        } catch (e) {
            return { ...err, message: "Ocurrió un error" };
        }
    }
    return err;
};

const descargar = (blob: Blob, nombre: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    a.click();
    window.URL.revokeObjectURL(url);
};

const TTL_MS = 60 * 1000;

class FacturacionService {

    private cacheHabilitada: { valor: boolean; vence: number } | null = null;

    /** Lo que cambia la configuracion de ARCA puede cambiar si se puede facturar. */
    private olvidar = <T>(r: T): T => {
        this.cacheHabilitada = null;
        return r;
    };

    // ── Configuracion de ARCA ──

    estadoArca(): Promise<any> {
        return http.get("/api/cuenta/arca").then((r: any) => r.data);
    }

    /** Actualiza solo los campos que se mandan. */
    actualizarArca(datos: any): Promise<any> {
        return http.post("/api/cuenta/arca", datos).then((r: any) => this.olvidar(r.data));
    }

    /** Genera la solicitud (CSR) y la descarga como `solicitud-arca.csr`. */
    generarCsr(): Promise<void> {
        return http.get("/api/cuenta/arca/generar-csr", { responseType: "blob" })
            .then((blob: any) => descargar(blob, "solicitud-arca.csr"))
            .catch(async (err: any) => { throw await errorDeBlob(err); });
    }

    subirCertificadoFirmado(file: File, password: string): Promise<any> {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("password", password);
        return http.post("/api/cuenta/arca/certificado-firmado", fd, { headers: { "Content-Type": "multipart/form-data" } })
            .then((r: any) => this.olvidar(r.data));
    }

    subirCertificado(file: File, password: string): Promise<any> {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("password", password);
        return http.post("/api/cuenta/arca/certificado", fd, { headers: { "Content-Type": "multipart/form-data" } })
            .then((r: any) => this.olvidar(r.data));
    }

    /** Siempre resuelve con el resultado (ok, o la etapa que fallo). */
    probarConexion(): Promise<any> {
        return http.post("/api/cuenta/arca/probar-conexion", {}).then((r: any) => this.olvidar(r.data));
    }

    // ── Facturas ──

    /** Si la cuenta tiene ARCA integrado y probado. Sin eso no se ofrece facturar. */
    habilitada(forzar = false): Promise<boolean> {
        const ahora = Date.now();
        if (!forzar && this.cacheHabilitada && this.cacheHabilitada.vence > ahora) {
            return Promise.resolve(this.cacheHabilitada.valor);
        }
        return http.get("/api/cuenta/facturas/habilitada")
            .then((r: any) => {
                this.cacheHabilitada = { valor: !!r.habilitada, vence: ahora + TTL_MS };
                return !!r.habilitada;
            })
            .catch(() => false);
    }

    buscar(searchDTO: any): Promise<any> {
        return http.post("/api/cuenta/facturas/buscar", searchDTO).then((r: any) => r.data);
    }

    /** La ultima factura de cada venta pedida, por id de venta. Las que no tienen factura no vienen. */
    deVentas(ventaIds: number[]): Promise<any> {
        if (!ventaIds.length) return Promise.resolve({});
        return http.post("/api/cuenta/facturas/de-ventas", { ids: ventaIds }).then((r: any) => r.data || {});
    }

    deVenta(ventaId: number): Promise<any[]> {
        return http.get(`/api/cuenta/facturas/venta/${ventaId}`).then((r: any) => r.data || []);
    }

    /**
     * Emite contra ARCA y devuelve la factura ya resuelta. Ojo: puede volver con la factura en RECHAZADA
     * (ARCA la rechazo) o en BORRADOR (no se pudo hablar con ARCA): hay que mirar `estado`.
     */
    facturarVenta(ventaId: number, datos: any): Promise<any> {
        return http.post(`/api/cuenta/facturas/venta/${ventaId}`, datos || {}).then((r: any) => r.data);
    }

    /** Solo para las que quedaron en BORRADOR por una falla de comunicacion con ARCA. */
    reintentar(id: number): Promise<any> {
        return http.post(`/api/cuenta/facturas/${id}/reintentar`, {}).then((r: any) => r.data);
    }

    anular(id: number): Promise<any> {
        return http.post(`/api/cuenta/facturas/${id}/anular`, {}).then((r: any) => r.data);
    }

    descargarPdf(f: any): Promise<void> {
        return http.get(`/api/cuenta/facturas/${f.id}/pdf`, { responseType: "blob" })
            .then((blob: any) => descargar(blob, `factura-${numeroFormateado(f).replace("—", String(f.id))}.pdf`))
            .catch(async (err: any) => { throw await errorDeBlob(err); });
    }
}

// ── Helpers de presentacion, para que cada pantalla no los reinvente ──

/** Letra del comprobante (A/B/C), que es como se lo nombra en la practica. */
export const letra = (f: any): string => {
    const m = f?.tipoComprobante?.match(/_([ABC])$/);
    return m ? m[1] : "";
};

/** Numero legal completo, con el formato 0001-00000123 que usa ARCA. */
export const numeroFormateado = (f: any): string => {
    if (f?.numero == null) return "—";
    return `${String(f.puntoVenta).padStart(4, "0")}-${String(f.numero).padStart(8, "0")}`;
};

export const estadoFacturaLabel = (estado: string): string => {
    switch (estado) {
        case "EMITIDA": return "Emitida";
        case "BORRADOR": return "Sin enviar a ARCA";
        case "RECHAZADA": return "Rechazada por ARCA";
        case "ANULADA": return "Anulada";
        default: return estado || "—";
    }
};

/** El mensaje del back, sea cual sea el codigo (alertService.error solo muestra los 500). */
export const mensajeDeError = (err: any, porDefecto: string): string =>
    (err && typeof err.message === "string" && err.message) || porDefecto;

/** dd/mm/aaaa de un LocalDate ("2026-10-13"). Con new Date() se interpreta en UTC y en Argentina da el dia anterior. */
export const fechaFactura = (fecha: string): string => {
    if (!fecha) return "—";
    const [a, m, d] = fecha.substring(0, 10).split("-");
    return `${d}/${m}/${a}`;
};

/** Con centavos: es un importe fiscal. */
export const importeFactura = (monto: any): string =>
    "$ " + Number(monto || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const facturacionService = new FacturacionService();

export default facturacionService;
